import React, { useState } from 'react';
import WishlistReserveModal from './WishlistReserveModal';

/* Привести введённую ссылку к абсолютному URL (добавить https:// при
   отсутствии схемы), чтобы ссылка на подарок открывалась корректно. */
function normalizeUrl(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  return 'https://' + s;
}

/* Format a wishlist price: groups digits by 3 and appends the currency
   symbol (default ₽). Accepts "12000", "12 000", "12 000 ₽", 12000, etc.
   currency — символ из block_content.wishlist.currency; пусто → ₽. */
export function formatPrice(raw, currency) {
  const cur = currency || "₽";
  if (raw == null || raw === '') return '';
  const digits = String(raw).replace(/\D/g, '');
  if (!digits) return String(raw);
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, '\u2009');
  return `${grouped}\u00A0${cur}`;
}

/**
 * Reusable wishlist with server-backed reservations.
 *
 * Props:
 *   items          — [{ id, name, price, reserved, reserved_by, reserved_at }]
 *   slug           — project slug (required for reservation POST)
 *   classes        — main class map (see defaults below)
 *   modalClasses   — class map for the reservation modal
 *   reserveApiPath — override for the reserve endpoint (default `/api/wishlist/public/:slug/reserve`)
 */
export default function Wishlist({ items, slug, classes = {}, modalClasses = {}, reserveApiPath, currency, reserveText }) {
  const cls = {
    list:         classes.list         || 'wl-list',
    item:         classes.item         || 'wl-item',
    itemBorder:   classes.itemBorder   || 'wl-item--border',
    row:          classes.row          || 'wl-row',
    name:         classes.name         || 'wl-name',
    price:        classes.price        || 'wl-price',
    btn:          classes.btn          || 'wl-btn',
    btnReserved:  classes.btnReserved  || 'wl-btn--reserved',
  };

  const [local, setLocal] = useState(() => {
    const init = {};
    (items || []).forEach(it => { init[it.id || it.name] = !!it.reserved; });
    return init;
  });
  const [prompt, setPrompt] = useState(null);
  const [nameInput, setNameInput] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');

  if (!items?.length) return null;

  const startReserve = (it) => {
    setErr(''); setNameInput('');
    setPrompt({ key: it.id || it.name, itemId: it.id, name: it.name });
  };
  const confirmReserve = async () => {
    if (!prompt) return;
    if (!nameInput.trim()) { setErr('Введите имя'); return; }
    if (!slug)             { setErr('Сайт не опубликован'); return; }
    setSending(true); setErr('');
    try {
      const url = reserveApiPath || `/api/wishlist/public/${slug}/reserve`;
      const r = await fetch(url, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ itemId: prompt.itemId, name: nameInput.trim() }),
      });
      if (r.status === 409) {
        setErr('Уже зарезервировано другим гостем');
        setLocal(p => ({ ...p, [prompt.key]: true }));
        setSending(false);
        return;
      }
      if (!r.ok) throw new Error('fail');
      setLocal(p => ({ ...p, [prompt.key]: true }));
      setPrompt(null);
    } catch { setErr('Не удалось зарезервировать. Попробуйте ещё раз.'); }
    finally { setSending(false); }
  };

  return (
    <div className={cls.list} data-cb-section="wishlist">
      {items.map((it, idx) => {
        const key = it.id || it.name;
        const isRes = local[key] || !!it.reserved;
        const href = normalizeUrl(it.link);
        /* Своя ссылка на подарок:
           • галочка вкл (linkOnButton) → ссылка открывается по кнопке,
             подарок НЕ резервируется;
           • галочка выкл → ссылка открывается по клику на название,
             кнопка работает как обычно (резерв). */
        const linkOnButton = !!href && !!it.linkOnButton;
        const nameIsLink   = !!href && !it.linkOnButton;
        const label = (it.buttonLabel || '').trim();
        return (
          <div key={key} className={`${cls.item}${idx > 0 ? ' ' + cls.itemBorder : ''}`}>
            <div className={cls.row}>
              {nameIsLink ? (
                <a className={cls.name} href={href} target="_blank" rel="noopener noreferrer"
                   style={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}>{it.name}</a>
              ) : (
                <span className={cls.name}>{it.name}</span>
              )}
              {it.price && <span className={cls.price}>{formatPrice(it.price, currency)}</span>}
            </div>
            {/* Кнопка-ссылка использует тот же <button> и класс, что и
                «Зарезервировать», — поэтому стиль идентичен. По клику
                открываем ссылку (без резервирования). */}
            {linkOnButton ? (
              <button type="button"
                className={cls.btn}
                onClick={() => window.open(href, '_blank', 'noopener,noreferrer')}>
                {label || 'Перейти'}
              </button>
            ) : (
              <button type="button"
                disabled={isRes}
                className={`${cls.btn}${isRes ? ' ' + cls.btnReserved : ''}`}
                onClick={() => !isRes && startReserve(it)}>
                {isRes ? 'Зарезервировано ✓' : (label || reserveText || 'Зарезервировать')}
              </button>
            )}
          </div>
        );
      })}

      <WishlistReserveModal
        item={prompt && { name: prompt.name }}
        value={nameInput}
        onChange={setNameInput}
        onConfirm={confirmReserve}
        onClose={() => setPrompt(null)}
        sending={sending}
        error={err}
        classes={modalClasses}
      />
    </div>
  );
}
